import BackendService from "../../../backend/controller/BackendService";
import CharacterModel from "../../../characters/model/CharacterModel";
import PlayerModel from "../../../player/model/PlayerModel";
import LlmMessagePrefixer from "../../../util/LlmMessagePrefixer";
import Conversation from "../../model/Conversation";
import GameConversationMessage, { GameMessageSender } from "../../model/GameConversationMessage";
import LlmConversationMessage, { LlmMessageSender } from "../../model/LlmConversationMessage";
import AiTurnConversationState from "./AiTurnConversationState";
import ConversationState from "./ConversationState";
import EndingTurnConversationState from "./EndingTurnConversationState";

export default class PlayerTurnConversationState extends ConversationState {
    constructor() {
        super();

        this._playerInputPromise = new Promise((resolve) => {
            this._resolvePromise = resolve;
        });
    }

    /**
     * 
     * @param {string} chapterId
     * @param {Conversation} conversation 
     * @param {CharacterModel[]} characters 
     * @param {BackendService} backendService 
     * @param {PlayerModel} playerModel
     */
    async execute(chapterId, conversation, characters, backendService, playerModel) {
        const playerName = playerModel.getTrimmedPlayerName();
        const playerInput = await this._playerInputPromise;

        const labeledPlayerInput = LlmMessagePrefixer.prefixMessageAsPlayer(playerInput, playerName);
        const playerLlmMessage = new LlmConversationMessage(LlmMessageSender.USER, labeledPlayerInput);
        const playerGameMessage = new GameConversationMessage(GameMessageSender.PLAYER, playerInput);

        let isEndingPhase = false;
        let trackerUpdates = [];

        try {
            this.dispatchEvent(ConversationState.SAVE_SNAPSHOT);

            const orchestrationResponse = await backendService.decideNextPhase({
                chapterId: chapterId,
                conversationId: conversation.id,
                phaseId: conversation.currentPhaseId,
                conversationHistory: [...conversation.conversationHistoryForLLM, playerLlmMessage],
            });

            // Persist current outcome where the conversation to end right now.
            const outcome = orchestrationResponse.outcome || conversation.outcome;
            conversation.setOutcome(outcome);

            // Set the next phase.
            const nextPhase = orchestrationResponse.phase || conversation.currentPhaseId;
            conversation.setPhase(nextPhase);

            // Persist the tracker updates.
            trackerUpdates = orchestrationResponse.trackerUpdates;

            // Notify of phase reasoning, so that we can show that in the debug tooling.
            const phaseReasoningEvent = new createjs.Event(PlayerTurnConversationState.EVENT_PHASE_REASONING);
            phaseReasoningEvent.reasoning = orchestrationResponse.reasoning;
            this.dispatchEvent(phaseReasoningEvent);

            // Persist if this phase is a finishing phase.
            if (orchestrationResponse.finish) {
                isEndingPhase = true;
            }
        } catch (error) {
            // Retry this turn.
            console.error("Failed to decide next phase", error);
            Promise.reject(error);
        }

        // Commit the player input to the conversation, bump the turns in this phase.
        conversation.addConversationHistory({
            llmConversationMessages: [playerLlmMessage],
            gameConversationMessages: [playerGameMessage],
        });

        conversation.bumpTurnsForCurrentPhase();

        // Handle character tracker updates.
        this._handleTrackerUpdates(trackerUpdates, characters);

        // Check if this phase is a finishing phase.
        if (isEndingPhase) {
            const nextState = new EndingTurnConversationState();
            return Promise.resolve(nextState);
        }

        // Check if we've reached a time-out.
        console.log(`Turn ${conversation.getTotalTurns()}/${conversation.maxPlayerTurns}. Turns to go: ${conversation.maxPlayerTurns - conversation.getTotalTurns()}.`);
        if (conversation.getTotalTurns() >= conversation.maxPlayerTurns) {
            const nextState = new EndingTurnConversationState();
            return Promise.resolve(nextState);
        }

        const nextState = new AiTurnConversationState();
        return Promise.resolve(nextState);
    }

    async reset() {
        // Reset the promise for the next player input.
        this._playerInputPromise = new Promise((resolve) => {
            this._resolvePromise = resolve;
        });
    }

    /**
     * 
     * @param {string} message 
     */
    handlePlayerInput(message) {
        this._resolvePromise(message);
    }

    /**
     * @param {*} trackerUpdates 
     * @param {CharacterModel[]} characters 
     * @returns 
     */
    _handleTrackerUpdates(trackerUpdates, characters) {
        for (const trackerUpdate of trackerUpdates) {
            const character = characters.find(character => character.id === trackerUpdate.character);
            if (!character) {
                console.error(`Character with id ${trackerUpdate.character} not found.`);
                continue;
            }

            for (const [trackerId, trackerValueDelta] of Object.entries(trackerUpdate.trackerDeltas)) {
                const oldTrackerValue = character.getTrackerById(trackerId).value;
                character.updateTrackerRelatively(trackerId, trackerValueDelta);
                const newTrackerValue = character.getTrackerById(trackerId).value;

                console.log(`[Tracker update] New value for tracker ${trackerId} on character ${character.name}: ${newTrackerValue}`);

                const trackerValueChangedEvent = new createjs.Event(PlayerTurnConversationState.EVENT_CHARACTER_TRACKER_VALUE_CHANGED);
                trackerValueChangedEvent.character = character;
                trackerValueChangedEvent.trackerId = trackerId;
                trackerValueChangedEvent.oldValue = oldTrackerValue;
                trackerValueChangedEvent.newValue = newTrackerValue;
                this.dispatchEvent(trackerValueChangedEvent);
            }
        }
    }
}

PlayerTurnConversationState.EVENT_CHARACTER_TRACKER_VALUE_CHANGED = 'characterTrackerValueChanged';
PlayerTurnConversationState.EVENT_PHASE_REASONING = 'PlayerTurnConversationState.EVENT_PHASE_REASONING';